import React, {Component} from 'react';
import {connect} from 'react-redux';

import FormFieldList from '../widgets/FormFieldList';
import FormField from '../widgets/FormField';
import Input from '../widgets/Input';
import Pillbox from '../widgets/Pillbox';
import Explanation from '../widgets/Explanation';
import NumberInput from '../widgets/NumberInput';
import Textarea from '../widgets/Textarea';
import HR from '../widgets/HR';
import Button from '../widgets/Button';
import ApprovalList from './ApprovalList';
import VersionStats from './VersionStats';
import FormRow from '../widgets/FormRow';
import FlexRow from '../widgets/FlexRow';
import Loading from '../widgets/Loading';
import SyllabusErrors from './SyllabusErrors';
import Dropzone from 'react-dropzone';

import {actions} from './syllabus.state';
import {actions as dirtyActions} from '../dirty.state';
import {actions as errorActions} from '../error.state';
import './VersionDetails.css';
import {parseItems, parseContent} from './csvParser';
import {checkItemErrors, checkContentErrors} from './syllabus.utils';
import {
	getFlattenedContent,
	getSelectedVersionID, getThisVersion, hasUniqueName,
	hasUniqueNumber
} from './version.selectors';
import {getRouterParams} from '../router.selectors';
import {
	CONTENT_EXAMPLE, CONTENT_HEADERS, exportFile, getContentAsCSV, getItemsAsCSV, ITEMS_EXAMPLE,
	ITEMS_HEADERS
} from './csvExporter';
import {getSelectedSet} from './set.selectors';

// https://stackoverflow.com/questions/7076042/what-mime-type-should-i-use-for-csv
const CSV_MIME_WHITELIST = {
	'text/csv': true, // RFC 7111 standard mimetype
	'text/plain': true,
	'application/vnd.ms-excel': true, // https://bugs.chromium.org/p/chromium/issues/detail?id=139105
	'text/comma-separated-values': true,
	'text/x-csv': true,
	'application/csv': true
};

export class VersionDetails extends Component {
	constructor(props) {
		super(props);
		
		this.state = {
			itemFile: null,
			contentFile: null,
			itemErrors: [],
			contentErrors: [],
			contentOrphans: []
		};
	}
	
	componentWillReceiveProps(nextProps) {
		if (this.props.versionID !== nextProps.versionID) {
			this.setState({
				itemFile: null,
				contentFile: null
			});
		}
	}
	
	clearItems = () => {
		this.setState({
			itemFile: null,
			contentFile: null,
			itemErrors: [],
			contentErrors: [],
			contentOrphans: []
		});
		this.props.updateItems([]);
	};
	
	handleFileSelectItems = files => {
		const file = files[0];
		
		if (!file || !file.type) return; // Didn't upload anything
		
		if (!CSV_MIME_WHITELIST[file.type]) {
			this.props.handleError("File error", `File must be a CSV, got ${file.type}`);
			return;
		}
		
		const reader = new FileReader();
		reader.readAsText(file);
		
		reader.onload = e => {
			const csv = e.target.result;
			const parsed = parseItems(csv);
			
			if (parsed.error) {
				this.props.handleError('File error', parsed.error);
				return;
			}
			
			this.props.updateItems(parsed.items);
			const itemErrors = checkItemErrors(parsed.items);
			
			this.setState({
				itemFile: file.name,
				contentFile: null,
				itemErrors,
				contentErrors: [],
				contentOrphans: []
			});
		}
	};
	
	handleFileSelectContent = files => {
		const file = files[0];
		
		if (!file || !file.type) return; // Didn't upload anything
		
		if (!CSV_MIME_WHITELIST[file.type]) {
			this.props.handleError("File error", `File must be a CSV, got ${file.type}`);
			return;
		}
		
		const reader = new FileReader();
		reader.readAsText(file);
		
		reader.onload = e => {
			const csv = e.target.result;
			
			const parsed = parseContent(csv);
			
			if (parsed.error) {
				this.props.handleError('File error', parsed.error);
				return;
			}
			
			const {items} = this.props.version;
			
			this.props.updateItems(
				items.map(item => ({
					...item,
					content: parsed.content.filter(c => c.item_code === item.code).map(c => {
						const newContent = {...c};
						delete newContent.item_code;
						return newContent;
					})
				}))
			);
			
			let [contentErrors, contentOrphans] = checkContentErrors(parsed.content, items);
			
			this.setState({
				contentFile: file.name,
				contentErrors,
				contentOrphans
			});
		}
	};
	
	downloadItemTemplate = async () => {
		const itemCsv = ITEMS_HEADERS + ITEMS_EXAMPLE;
		exportFile('SyllabusItems.csv', itemCsv);
	};
	
	downloadContentTemplate = async () => {
		const contentCsv = CONTENT_HEADERS + CONTENT_EXAMPLE;
		exportFile('SyllabusContent.csv', contentCsv)
	};
	
	downloadItems = () => {
		exportFile(`${this.props.set.name} (${this.props.version.name}) items.csv`, getItemsAsCSV(this.props.version.items));
	};
	
	downloadContent = () => {
		exportFile(`${this.props.set.name} (${this.props.version.name}) content.csv`, getContentAsCSV(this.props.version.items));
	};
	
	render() {
		if (this.props.loading) return (<section className="VersionDetails"><Loading /></section>);
		
		if (!this.props.version || !this.props.version.uuid) return null; // Do not render if version not found
		
		const {
			version: {uuid, name, number, status, approvals, notes, items} = {},
			loading, updateName, updateNumber, updateStatus, updateApprovals, setID,
			nameIsUnique, numberIsUnique, flattenedContent, updateNotes
		} = this.props;
		const {itemFile, contentFile, itemErrors, contentErrors, contentOrphans} = this.state;
		const disabled = status !== 'draft';
		
		return (
			<section className="VersionDetails">
				<FormFieldList>
					<FormField label="Version name" error={nameIsUnique ? null : 'Version name must be unique across all versions'}>
						<Input type="text" value={name} onChange={e => updateName(e.target.value)} disabled={disabled} maxLength={256}/>
					</FormField>
					<FormField label="Internal version number" error={numberIsUnique ? null : 'Version number must be unique across all versions'}>
						<NumberInput value={number} onChange={number => updateNumber(number)} disabled={disabled} min={1} max={32767}/>
					</FormField>
					<FormField label="Approval" explanation={(
						approvals && approvals.length ? <p>Approvers: {approvals.join(', ')}</p> : <p>No-one has approved this yet</p>
					)}>
						<ApprovalList disabled={disabled} approvals={approvals} updateApprovals={updateApprovals}/>
					</FormField>
					<FormField label="Status" explanation={(
						<p>Once published, a version cannot be modified!</p>
					)}>
						<Pillbox
							options={disabled ? ['published', 'hidden'] : approvals && approvals.length && approvals.length >= 2 ? ['draft', 'published', 'hidden'] : ['draft']}
							selected={status}
							onSelect={status => updateStatus(status)}
						/>
					</FormField>
					<FormField label="Notes">
						<Textarea
							value={notes || ''}
							maxLength={10000}
							onChange={e => updateNotes(e.target.value)}
						/>
					</FormField>
					<HR/>
					
					<FormRow>
						<FlexRow>
							<Button disabled={((!items || !items.length) && (!flattenedContent || !flattenedContent.length)) || disabled} onClick={this.clearItems}>Clear</Button>
							<Button className="Download" onClick={this.downloadItemTemplate}>Item CSV Example</Button>
							<Button className="Download" onClick={this.downloadContentTemplate}>Content CSV Example</Button>
						</FlexRow>
					</FormRow>
					
					<FormField label="Import Items">
						{/*Important that each render is a new dropzone component (hence key prop) because we don't want it to be stateful (which it is by default)*/}
						<div>
							<Dropzone
								key={`${itemFile}_${new Date().getTime()}`}
								className={`FileInput ${disabled ? 'disabled' : ''}`}
								accept=".csv"
								multiple={false}
								disableClick={true}
								onDrop={this.handleFileSelectItems}
								disabled={disabled}>
								<p>{disabled ? 'Uploads are disabled' : itemFile || 'Click or drag/drop a file here to upload'}</p>
							</Dropzone>
							{items && items.length ? (
								<div className="VersionDetails-redownload">
									<span>{items.length} syllabus items imported</span> — <Button onClick={this.downloadItems}>Download</Button>
								</div>
							) : undefined}
						</div>
					</FormField>
					<FormField label="Import Content">
						<div>
							{/*Important that each render is a new dropzone component (hence key prop) because we don't want it to be stateful (which it is by default)*/}
							<Dropzone
								key={`${contentFile}_${new Date().getTime()}`}
								className={`FileInput ${(!items || !items.length || disabled) ? 'disabled' : ''}`}
								accept=".csv"
								multiple={false}
								disableClick={true}
								onDrop={this.handleFileSelectContent}
								disabled={!items || !items.length || disabled}>
								<p>{disabled ? 'Uploads are disabled' : contentFile || 'Click or drag/drop a file here to upload'}</p>
							</Dropzone>
							{flattenedContent && flattenedContent.length ? (
								<div className="VersionDetails-redownload">
									<span>{flattenedContent.length} syllabus content imported</span> — <Button onClick={this.downloadContent}>Download</Button>
								</div>
							) : undefined}
						</div>
					</FormField>
					<Explanation>
						<p>Importing will replace the current data.</p>
						<p>Upload a valid CSV document; use the download for a template.</p>
					</Explanation>
					
					{items && items.length ? <FormRow>
						<VersionStats newItems={items} setID={setID} versionNumber={number} itemErrors={itemErrors} contentErrors={contentErrors} contentOrphans={contentOrphans}/>
					</FormRow> : null}
					
					{((itemErrors && itemErrors.length) || (contentErrors && contentErrors.length) || (contentOrphans && contentOrphans.length)) ? <FormRow>
						<Explanation>
							<p>There are errors in your data. To save, first correct these errors.</p>
							<p>You can check off items as you correct them in your source data. Checkbox state is not stored.</p>
						</Explanation>
						<SyllabusErrors itemErrors={itemErrors} contentErrors={contentErrors} contentOrphans={contentOrphans}/>
					</FormRow> : null}
				</FormFieldList>
			</section>
		);
	}
}

const ConnectedVersionDetails = connect(
	(state, ownProps) => ({
		setID: getRouterParams(state, ownProps).setID,
		versionID: getSelectedVersionID(state, ownProps),
		version: getThisVersion(state, ownProps),
		set: getSelectedSet(state, ownProps),
		flattenedContent: getFlattenedContent(state, ownProps),
		loading: state.syllabus.versions.retrieving,
		nameIsUnique: hasUniqueName(state, ownProps),
		numberIsUnique: hasUniqueNumber(state, ownProps)
	}),
	(dispatch, {match: {params: {versionID}}}) => ({
		updateName: name => {
			dispatch(actions.versionUpdate({name, versionID}));
			dispatch(dirtyActions.dirtySet(versionID));
		},
		updateNumber: number => {
			dispatch(actions.versionUpdate({number, versionID}));
			dispatch(dirtyActions.dirtySet(versionID));
		},
		updateStatus: status => {
			dispatch(actions.versionUpdate({status, versionID}));
			dispatch(dirtyActions.dirtySet(versionID));
		},
		updateApprovals: approvals => {
			dispatch(actions.versionUpdate({approvals, versionID}));
			dispatch(dirtyActions.dirtySet(versionID));
		},
		updateNotes: notes => {
			dispatch(actions.versionUpdate({notes, versionID}));
			dispatch(dirtyActions.dirtySet(versionID));
		},
		updateItems: (items, setDirty = true) => {
			dispatch(actions.versionUpdate({items, versionID}));
			if (setDirty) dispatch(dirtyActions.dirtySet(versionID));
		},
		handleError: (title, message) => dispatch(errorActions.errorSet({title, message}))
	})
)(VersionDetails);

export default ConnectedVersionDetails;


// WEBPACK FOOTER //
// ./src/syllabus/VersionDetails.js